import { UserSettings } from "@/hooks/use-settings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SettingRow = ({ label, description, children }: { label: string; description: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="min-w-0">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <p className="text-[11px] text-muted-foreground">{description}</p>
    </div>
    {children}
  </div>
);

interface Props {
  settings: UserSettings;
  updateSetting: <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => void;
}

const PipingSettings = ({ settings, updateSetting }: Props) => (
  <section className="space-y-3">
    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Piping Defaults</h2>
    <div className="trade-card space-y-4">
      <SettingRow label="Pipe Material" description="Default material for friction loss and sizing calcs">
        <Select value={settings.pipeMaterial} onValueChange={(v) => updateSetting("pipeMaterial", v as UserSettings["pipeMaterial"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              <span>Steel (Sch 40)</span>
              <span className="ml-2 text-[10px] text-muted-foreground">C=120</span>
            </SelectItem>
            <SelectItem value="1">
              <span>Copper (Type L)</span>
              <span className="ml-2 text-[10px] text-muted-foreground">C=140</span>
            </SelectItem>
            <SelectItem value="2">
              <span>PVC (Sch 40)</span>
              <span className="ml-2 text-[10px] text-muted-foreground">C=150</span>
            </SelectItem>
            <SelectItem value="3">
              <span>CPVC</span>
              <span className="ml-2 text-[10px] text-muted-foreground">C=150</span>
            </SelectItem>
            <SelectItem value="4">
              <span>Stainless Steel</span>
              <span className="ml-2 text-[10px] text-muted-foreground">C=120</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>

      <SettingRow label="Fitting Angle" description="Default angle for offset calculations">
        <Select value={settings.fittingAngle} onValueChange={(v) => updateSetting("fittingAngle", v as UserSettings["fittingAngle"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="22.5">22½°</SelectItem>
            <SelectItem value="30">30°</SelectItem>
            <SelectItem value="45">45°</SelectItem>
            <SelectItem value="60">60°</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>

      <SettingRow label="Drain Slope" description="Default slope for drain pipe sizing">
        <Select value={settings.drainSlope} onValueChange={(v) => updateSetting("drainSlope", v as UserSettings["drainSlope"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1/16">1/16" per ft</SelectItem>
            <SelectItem value="1/8">1/8" per ft</SelectItem>
            <SelectItem value="1/4">1/4" per ft</SelectItem>
            <SelectItem value="1/2">1/2" per ft</SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>

      <SettingRow label="Velocity Preset" description="Default velocity range for GPM sizing">
        <Select value={settings.pipeVelocityPreset} onValueChange={(v) => updateSetting("pipeVelocityPreset", v as UserSettings["pipeVelocityPreset"])}>
          <SelectTrigger className="w-36 bg-secondary border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0">
              <span>HVAC / Comfort</span>
              <span className="ml-2 text-[10px] text-muted-foreground">4–8 ft/s</span>
            </SelectItem>
            <SelectItem value="1">
              <span>Domestic Water</span>
              <span className="ml-2 text-[10px] text-muted-foreground">3–5 ft/s</span>
            </SelectItem>
            <SelectItem value="2">
              <span>Process / Industrial</span>
              <span className="ml-2 text-[10px] text-muted-foreground">5–10 ft/s</span>
            </SelectItem>
            <SelectItem value="3">
              <span>Fire Protection</span>
              <span className="ml-2 text-[10px] text-muted-foreground">≤15 ft/s</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </SettingRow>
    </div>
  </section>
);

export default PipingSettings;
